import type { SetStateAction, Dispatch } from "react";

/**
 * Given a setState function whose handler's first argument is an object,
 * this function returns a function that sets a the state to the value of a
 * node from the handler's first argument.
 *
 * @example
 * const [name, setName] = React.useState("Craig");
 * const props = { name, ...rest };
 * <PersonInput id="name" {...props} onChange={setStateFromNode("name", setName)} />
 *
 * @example
 * // The type signature of the returned function is inferred from the first property
 * type MyFn = (value: { name: string, age: number }) => void;
 * const myFn: MyFn = setStateFromNode("name", setName); // ✅
 * const myFn2: MyFn = setStateFromNode("foo", setName); // 🛑 "foo" doesn't exist on `MyFn`'s first parameter
 */
export function setStateFromNode<
  T extends Record<string, any>,
  K extends keyof T
>(node: K, setState: Dispatch<SetStateAction<T[K]>>): (value: T) => void {
  return (value) => {
    setState(value[node]);
  };
}
